import { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import Paginator from '../../../components/ui/Paginator';
import Message from '../../../components/ui/Message';
import { WalletConfigLink } from './wallet/WalletConfigLink';
import FormModal from '../../../components/ui/FormModal';
import { LoyaltyContext } from '../../../context/loyalty/LoyaltyContext';
import { LoyaltyClient, LoyaltyHistory } from '../../../models/interfaces';

const PAGE_SIZE = 10;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyClient = { nombre: '', correo: '', plataforma: 'android', tipoPrograma: 'puntos' };

const formatDate = (value: any, withTime = false) =>
    value ? moment(value).format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY') : '-';

const isStamps = (client: LoyaltyClient) => String(client.tipoPrograma ?? '').toLowerCase() === 'sellos';

const transactionBadge = (type: string) => {
    switch (String(type ?? '').toUpperCase()) {
        case 'COMPRA': return 'bg-success-subtle text-success-emphasis';
        case 'CANJE': return 'bg-primary-subtle text-primary-emphasis';
        case 'ANULACION': return 'bg-danger-subtle text-danger-emphasis';
        default: return 'bg-secondary-subtle text-secondary-emphasis';
    }
};

export const LoyaltyClientsTab = () => {
    const { clients, loadClients, addClient, deleteClient, resendCard, getClientHistory } = useContext(LoyaltyContext);

    const [isLoading, setIsLoading] = useState(false);
    const [buscar, setBuscar] = useState('');
    const [programFilter, setProgramFilter] = useState<'all' | 'puntos' | 'sellos'>('all');
    const [pageActual, setPageActual] = useState(1);

    // Registro
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyClient);
    const [formError, setFormError] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);

    // Historial
    const [historyClient, setHistoryClient] = useState<LoyaltyClient | null>(null);
    const [history, setHistory] = useState<LoyaltyHistory | null>(null);
    const [historyError, setHistoryError] = useState('');
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Acciones
    const [busyId, setBusyId] = useState<string | null>(null);
    const [toDelete, setToDelete] = useState<LoyaltyClient | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const refresh = async () => {
        setIsLoading(true);
        const resp = await loadClients();
        setIsLoading(false);
        if (resp !== 'success') setError(resp);
    };

    useEffect(() => {
        refresh();
    }, []);

    useEffect(() => {
        setPageActual(1);
    }, [buscar, programFilter]);

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        return clients
            .filter(c => programFilter === 'all' || String(c.tipoPrograma ?? '').toLowerCase() === programFilter)
            .filter(c => !term ||
                [c.nombre, c.correo, c.loyaltyMemberId].some(v => String(v ?? '').toLowerCase().includes(term)));
    }, [clients, buscar, programFilter]);

    const totalRecords = filteredData.length;
    const totalPage = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
    const pageItems = filteredData.slice((pageActual - 1) * PAGE_SIZE, pageActual * PAGE_SIZE);
    const startIndex = totalRecords === 0 ? 0 : (pageActual - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(pageActual * PAGE_SIZE, totalRecords);

    useEffect(() => {
        if (pageActual > totalPage) setPageActual(totalPage);
    }, [totalPage]);

    // -------------------- Registro --------------------

    const openNew = () => {
        setForm(emptyClient);
        setFormError('');
        setSubmitted(false);
        setShowForm(true);
    };

    const formErrors = {
        nombre: !form.nombre.trim(),
        correo: !EMAIL_REGEX.test(form.correo.trim()),
    };
    const invalid = (field: keyof typeof formErrors) => submitted && formErrors[field] ? 'is-invalid' : '';

    const handleSave = async () => {
        setSubmitted(true);
        setFormError('');
        if (Object.values(formErrors).some(Boolean)) return;

        setSaving(true);
        const resp = await addClient({
            nombre: form.nombre.trim(),
            correo: form.correo.trim(),
            plataforma: form.plataforma,
            tipoPrograma: form.tipoPrograma,
        } as any);
        setSaving(false);

        if (resp !== 'success') {
            setFormError(resp);
            return;
        }
        setShowForm(false);
        setSuccess(`Se registró a ${form.nombre.trim()} y se envió la tarjeta a ${form.correo.trim()}.`);
        refresh();
    };

    // -------------------- Acciones --------------------

    const openHistory = async (client: LoyaltyClient) => {
        setHistoryClient(client);
        setHistory(null);
        setHistoryError('');
        setLoadingHistory(true);
        const resp = await getClientHistory(client.loyaltyMemberId);
        setLoadingHistory(false);
        if (resp.error) setHistoryError(resp.error);
        else setHistory(resp.history ?? null);
    };

    const handleResend = async (client: LoyaltyClient) => {
        setBusyId(client.loyaltyMemberId);
        const resp = await resendCard(client.loyaltyMemberId, client.correo);
        setBusyId(null);
        if (resp !== 'success') setError(resp);
        else setSuccess(`Se envió la tarjeta de ${client.nombre} a ${client.correo}.`);
    };

    const processDelete = async () => {
        const client = toDelete;
        setToDelete(null);
        if (!client) return;
        setBusyId(client.loyaltyMemberId);
        const resp = await deleteClient(client.loyaltyMemberId);
        setBusyId(null);
        if (resp !== 'success') {
            setError(resp);
        } else {
            setSuccess(`${client.nombre} fue eliminado del programa de lealtad.`);
            refresh();
        }
    };

    // -------------------- Render --------------------

    const renderBalance = (client: LoyaltyClient) => {
        if (isStamps(client)) {
            const meta = Number(client.sellosMeta ?? 0);
            const current = Number(client.sellosActuales ?? 0);
            const percent = meta > 0 ? Math.min(100, (current / meta) * 100) : 0;
            return (
                <div style={{ minWidth: 120 }}>
                    <div className="small fw-semibold">{current}{meta > 0 ? ` / ${meta}` : ''} sellos</div>
                    {meta > 0 && (
                        <div className="progress" style={{ height: 6 }}>
                            <div className="progress-bar bg-success" style={{ width: `${percent}%` }} />
                        </div>
                    )}
                </div>
            );
        }
        return <span className="fw-semibold">{Number(client.puntosActuales ?? 0).toLocaleString('en-US')} pts</span>;
    };

    return (
        <>
            <div className="row g-2 mb-3 align-items-center">
                <div className="col-md-6">
                    <div className="input-group">
                        <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                        <input
                            className="form-control"
                            placeholder="Buscar por nombre, correo o ID de miembro..."
                            value={buscar}
                            onChange={(e) => setBuscar(e.target.value)}
                        />
                        {buscar && (
                            <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                <i className="bi bi-x-lg" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <select className="form-select" value={programFilter} onChange={(e) => setProgramFilter(e.target.value as any)}>
                        <option value="all">Todos los programas</option>
                        <option value="puntos">Puntos</option>
                        <option value="sellos">Sellos</option>
                    </select>
                </div>
                <div className="col-6 col-md-3 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary" title="Actualizar" onClick={refresh} disabled={isLoading}>
                        <i className="bi bi-arrow-clockwise" />
                    </button>
                    <button type="button" className="btn btn-primary" onClick={openNew}>
                        <i className="bi bi-person-plus me-1" /> Nuevo cliente
                    </button>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-hover align-middle">
                    <thead>
                        <tr className="text-muted small text-uppercase">
                            <th>Cliente</th>
                            <th>ID de miembro</th>
                            <th>Programa</th>
                            <th>Saldo</th>
                            <th>Plataforma</th>
                            <th>Registro</th>
                            <th style={{ textAlign: 'center' }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={7} className="text-center py-4">
                                <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                            </td></tr>
                        ) : pageItems.length > 0 ? (
                            pageItems.map(client => {
                                const busy = busyId === client.loyaltyMemberId;
                                const ios = String(client.plataforma ?? '').toLowerCase() === 'ios';
                                return (
                                    <tr key={client.loyaltyMemberId}>
                                        <td>
                                            <div className="fw-medium">{client.nombre}</div>
                                            <div className="text-muted small">{client.correo}</div>
                                        </td>
                                        <td className="text-muted" style={{ fontFamily: 'monospace' }}>{client.loyaltyMemberId}</td>
                                        <td>
                                            <span className={`badge rounded-pill ${isStamps(client) ? 'bg-info-subtle text-info-emphasis' : 'bg-primary-subtle text-primary-emphasis'}`}>
                                                {isStamps(client) ? 'Sellos' : 'Puntos'}
                                            </span>
                                        </td>
                                        <td>{renderBalance(client)}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <i className={`bi ${ios ? 'bi-apple' : 'bi-android2'} me-1 text-muted`} />
                                            {ios ? 'iOS' : 'Android'}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(client.fechaRegistro)}</td>
                                        <td style={{ textAlign: 'center', width: 150, whiteSpace: 'nowrap' }}>
                                            <button type="button" className="btn btn-sm btn-outline-secondary" title="Ver historial" onClick={() => openHistory(client)} disabled={busy}>
                                                <i className="bi bi-clock-history" />
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-primary ms-1" title="Reenviar tarjeta por correo" onClick={() => handleResend(client)} disabled={busy}>
                                                {busy ? <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} /> : <i className="bi bi-envelope" />}
                                            </button>
                                            <button type="button" className="btn btn-sm btn-danger ms-1" title="Eliminar del programa" onClick={() => setToDelete(client)} disabled={busy}>
                                                <i className="bi bi-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan={7} className="text-center py-4 text-muted">
                                {buscar || programFilter !== 'all' ? 'No se encontraron clientes para el filtro seleccionado.' : 'No hay clientes registrados en el programa de lealtad.'}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                <span className="text-muted small">
                    {!isLoading && totalRecords > 0 ? `Mostrando ${startIndex}-${endIndex} de ${totalRecords} clientes` : ''}
                </span>
                {totalPage > 1 ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
            </div>

            {/* Nuevo cliente */}
            {showForm && (
                <FormModal
                    title="Nuevo cliente"
                    subtitle="Se registrará en el programa y se le enviará la tarjeta Wallet por correo."
                    onClose={() => setShowForm(false)}
                    onSave={handleSave}
                    saving={saving}
                    saveLabel="Registrar"
                    error={formError && <>{formError}<div><WalletConfigLink message={formError} /></div></>}
                >
                    <div className="row g-3">
                        <div className="col-12">
                            <label className="form-label small text-muted mb-1">Nombre *</label>
                            <input className={`form-control ${invalid('nombre')}`} value={form.nombre} autoFocus
                                onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))} />
                            <div className="invalid-feedback">El nombre es requerido.</div>
                        </div>
                        <div className="col-12">
                            <label className="form-label small text-muted mb-1">Correo *</label>
                            <input type="email" className={`form-control ${invalid('correo')}`} value={form.correo}
                                onChange={(e) => setForm(prev => ({ ...prev, correo: e.target.value }))} />
                            <div className="invalid-feedback">Ingrese un correo válido.</div>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label small text-muted mb-1">Plataforma *</label>
                            <div className="btn-group w-100" role="group">
                                {[{ value: 'android', label: 'Android', icon: 'bi-android2' }, { value: 'ios', label: 'iOS', icon: 'bi-apple' }].map(opt => (
                                    <button key={opt.value} type="button"
                                        className={`btn ${form.plataforma === opt.value ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setForm(prev => ({ ...prev, plataforma: opt.value }))}>
                                        <i className={`bi ${opt.icon} me-1`} />{opt.label}
                                    </button>
                                ))}
                            </div>
                            <div className="form-text">Define si la tarjeta es de Google Wallet o Apple Wallet.</div>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label small text-muted mb-1">Programa *</label>
                            <select className="form-select" value={form.tipoPrograma} onChange={(e) => setForm(prev => ({ ...prev, tipoPrograma: e.target.value }))}>
                                <option value="puntos">Puntos</option>
                                <option value="sellos">Sellos</option>
                            </select>
                        </div>
                    </div>
                </FormModal>
            )}

            {/* Historial */}
            {historyClient && (
                <FormModal
                    title={`Historial de ${historyClient.nombre}`}
                    subtitle={<span style={{ fontFamily: 'monospace' }}>{historyClient.loyaltyMemberId}</span>}
                    onClose={() => setHistoryClient(null)}
                    error={historyError}
                    size="lg"
                >
                    {loadingHistory ? (
                        <div className="text-center py-4"><span className="spinner-border spinner-border-sm me-2" /> Cargando historial...</div>
                    ) : history && (
                        <>
                            <div className="d-flex flex-wrap gap-3 mb-3">
                                <div className="border rounded px-3 py-2">
                                    <div className="text-muted small">{isStamps(historyClient) ? 'Sellos actuales' : 'Puntos actuales'}</div>
                                    <div className="fs-5 fw-semibold">
                                        {isStamps(historyClient)
                                            ? `${history.sellosActuales ?? historyClient.sellosActuales ?? 0}${historyClient.sellosMeta ? ` / ${historyClient.sellosMeta}` : ''}`
                                            : Number(history.puntosActuales ?? 0).toLocaleString('en-US')}
                                    </div>
                                </div>
                                <div className="border rounded px-3 py-2">
                                    <div className="text-muted small">Movimientos</div>
                                    <div className="fs-5 fw-semibold">{history.transacciones?.length ?? 0}</div>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-sm table-hover align-middle mb-0">
                                    <thead>
                                        <tr className="text-muted small text-uppercase">
                                            <th>Fecha</th>
                                            <th>Tipo</th>
                                            <th>Detalle</th>
                                            <th className="text-end">{isStamps(historyClient) ? 'Cantidad' : 'Puntos'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(history.transacciones ?? []).length > 0 ? history.transacciones.map((tx, idx) => {
                                            const type = String(tx.tipo ?? '').toUpperCase();
                                            const negative = type === 'CANJE' || type === 'ANULACION';
                                            return (
                                                <tr key={tx.idTransaccion ?? idx}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{tx.fecha ? moment(tx.fecha, ['YYYY-MM-DD HH:mm', moment.ISO_8601]).format('DD/MM/YYYY HH:mm') : '-'}</td>
                                                    <td><span className={`badge rounded-pill ${transactionBadge(type)}`}>{type || '-'}</span></td>
                                                    <td className="small">{tx.detalle || '-'}</td>
                                                    <td className={`text-end fw-semibold ${negative ? 'text-danger' : 'text-success'}`}>
                                                        {negative ? '-' : '+'}{Math.abs(Number(tx.puntos ?? 0)).toLocaleString('en-US')}
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr><td colSpan={4} className="text-center py-4 text-muted">El cliente aún no tiene movimientos.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </FormModal>
            )}

            {toDelete && (
                <Message
                    type="success"
                    title="Atención"
                    message={`¿Está seguro de que desea eliminar a ${toDelete.nombre} del programa de lealtad?`}
                    closeDialog={() => setToDelete(null)}
                    showCancel={true}
                    handleCancel={() => setToDelete(null)}
                    handleOk={processDelete}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
            {error.length > 0 && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)">
                    <WalletConfigLink message={error} />
                </Message>
            )}
            {success.length > 0 && (
                <Message type="success" title="Exitoso" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
        </>
    );
};
